/**
 * A aba "Temas e subtemas" de Ajustes: como a lista é agrupada, filtrada
 * e rotulada (2026-10-28). Puro e testado; a página só desenha.
 *
 * Nomes: na tela, `ThemeGroup` é o TEMA e `Theme` é o SUBTEMA.
 *
 * A lista é um bloco por tema, com os subtemas dele dentro. Três regras
 * que a pessoa vê:
 *
 *  - **Filtro de marca** recorta os subtemas; tema que ficou sem nenhum
 *    some (um tema vazio não responde "onde está o subtema da marca X?").
 *  - **Busca** casa nome do tema, nome do subtema, marca e termos. Se o
 *    NOME DO TEMA casou, mostra todos os subtemas dele; senão só os que
 *    casaram. Tema sem resultado some — menos o que casou pelo nome, mesmo
 *    sem subtema nenhum.
 *  - **Subtema órfão não some**: se o tema dele não foi carregado (tema
 *    desativado com "Mostrar desativados" desligado), ele cai num bloco
 *    "Tema desativado" em vez de desaparecer da tela com a linha viva.
 */

import type { Theme, ThemeGroup } from "@/shared/api/api-client";
import { filterBySearch } from "@/shared/utils/text-search";

/** Quantos subtemas um tema aberto mostra antes do "ver todos". */
export const THEME_BLOCK_PREVIEW = 10;

export type ThemeBlock = {
  /** Estável entre renders: `g<id>` do tema, `o<id>` do órfão. */
  key: string;
  groupId: number;
  /** `null` = o tema não está carregado (órfão). */
  group: ThemeGroup | null;
  name: string;
  /** Os subtemas que passam nos filtros. */
  themes: Theme[];
  /** Quantos subtemas o tema tem sem filtro nenhum (na lista carregada). */
  totalThemes: number;
};

export type ThemeBlockFilters = {
  search: string;
  /** `null` = todas as marcas. */
  brandId: number | null;
};

export function isFiltering({ search, brandId }: ThemeBlockFilters): boolean {
  return search.trim() !== "" || brandId !== null;
}

export function buildThemeBlocks(
  themeGroups: ThemeGroup[],
  themes: Theme[],
  filters: ThemeBlockFilters,
  brandName: (id: number) => string
): ThemeBlock[] {
  const known = new Set(themeGroups.map(group => group.id));
  const byGroup = new Map<number, Theme[]>();
  for (const theme of themes) {
    const list = byGroup.get(theme.theme_group_id) ?? [];
    list.push(theme);
    byGroup.set(theme.theme_group_id, list);
  }

  const raw: { group: ThemeGroup | null; groupId: number; themes: Theme[] }[] = [
    ...themeGroups.map(group => ({
      group,
      groupId: group.id,
      themes: byGroup.get(group.id) ?? []
    })),
    ...[...byGroup.entries()]
      .filter(([groupId]) => !known.has(groupId))
      .map(([groupId, list]) => ({ group: null, groupId, themes: list }))
  ];

  const searching = filters.search.trim() !== "";
  const filtering = isFiltering(filters);

  const blocks: ThemeBlock[] = [];
  for (const entry of raw) {
    const name = entry.group?.name ?? "Tema desativado";
    const ofBrand =
      filters.brandId === null
        ? entry.themes
        : entry.themes.filter(theme => theme.brand_id === filters.brandId);

    const nameMatches =
      searching &&
      filterBySearch([name], filters.search, value => [value]).length > 0;
    const shown = !searching || nameMatches
      ? ofBrand
      : filterBySearch(ofBrand, filters.search, theme => [
          theme.name,
          theme.terms,
          brandName(theme.brand_id),
          // "economia inflação" acha o subtema pelas duas palavras: uma
          // é do tema e a outra é dele.
          name
        ]);

    if (filtering) {
      // Com filtro de marca, só fica o tema que tem subtema dela. Só com
      // busca, o tema que casou pelo nome fica mesmo vazio.
      const keeps =
        filters.brandId !== null ? shown.length > 0 : nameMatches || shown.length > 0;
      if (!keeps) continue;
    }

    blocks.push({
      key: entry.group ? `g${entry.groupId}` : `o${entry.groupId}`,
      groupId: entry.groupId,
      group: entry.group,
      name,
      themes: shown,
      totalThemes: entry.themes.length
    });
  }
  return blocks;
}

/** "sem peso" / "peso 1,5" — a vírgula é a da tela, não a do JSON. */
export function weightLabel(weight: number | null): string {
  return weight === null ? "sem peso" : `peso ${String(weight).replace(".", ",")}`;
}

/** "3 subtemas", "1 subtema", e "2 de 12 subtemas" quando o filtro cortou. */
export function themeCountLabel(shown: number, total: number): string {
  const word = (n: number) => (n === 1 ? "subtema" : "subtemas");
  return shown === total ? `${shown} ${word(shown)}` : `${shown} de ${total} ${word(total)}`;
}
